import {
  ActionButtons,
  AppToasterV2,
  FileDocument,
  FileField,
  FileFieldProps,
  FileFieldValue,
  FormFieldErrorContainer,
  LoadingV2,
  useDocument,
} from "@ucl/library";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBoltApiClient } from "@bolt/core";
import { createBoltDocumentApiClient } from "../../lib/apiClient/boltDocumentApiClient";
import { BoltDocument } from "../../types/types";
import {
  FileInfo,
  UploaderComponent,
  UploaderModel,
} from "@syncfusion/ej2-react-inputs";
import { faDownload, faTrash } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";

export interface BoltDocumentFileUploaderProps
  extends Omit<
    FileFieldProps,
    | "type"
    | "uploaderConfig"
    | "documentsFolderName"
    | "onCompleteRemoving"
    | "onSubmit"
  > {
  entityType?: string;
  entityId?: string;
  onRemoveDocument?: (documentId: string) => void;
  onSubmit?: (value: FileFieldValue) => void;
}

export function BoltDocumentFileUploader(props: BoltDocumentFileUploaderProps) {
  const boltApiClient = useBoltApiClient();
  const documentsRef = useRef<BoltDocument[]>([]);
  const documentIdsRef = useRef<string[]>([]);
  const [ready, setReady] = useState(false);
  const fileFieldRef = useRef<UploaderComponent>(null);
  const [fileFieldKey, setFileFieldKey] = useState<string>("");
  const documentModule = useDocument(boltApiClient);

  const boltDocumentApiClient = useMemo(
    () => createBoltDocumentApiClient(boltApiClient),
    [boltApiClient]
  );

  const getDocuments = useCallback(
    async (documentIds: string[]) => {
      const docs = await boltDocumentApiClient.getDocuments(documentIds);
      documentsRef.current = [...docs];
      documentIdsRef.current = [...documentIds];
      setReady(true);
    },
    [boltDocumentApiClient]
  );

  useEffect(() => {
    if (props.value) {
      // Sanitize values to fetch
      const values = sanitizeValuesToFetch(props.value.split(","));
      if (values.length === 0) {
        return;
      }

      getDocuments(values);
    } else {
      setReady(true);
    }
  }, [getDocuments]);

  const fileFieldProps = useMemo(
    () => ({
      type: "File" as const,
      documentsFolderName: "boltdocuments",
      className: "bolt-document-file-uploader",
      label: props.label,
      documentId: props.documentId,
      value: undefined,
      allowMultiple: props.allowMultiple,
      onCompleteRemoving: (doc?: FileDocument) => {
        props.onRemoveDocument?.(doc?.id || "");
      },
      uploaderConfig: {
        allowedExtensions: ".pdf",
        showFileList: true,
        asyncSettings: {
          chunkSize: 102400,
          saveUrl: `${boltApiClient?.baseApiUrl}/documents/upload`,
          removeUrl: `${boltApiClient?.baseApiUrl}/documents/remove`,
        },
      } as UploaderModel,
      onCustomFormData: () => {
        return {
          entityType: props.entityType,
          entityId: props.entityId,
        };
      },
    }),
    [
      boltApiClient?.baseApiUrl,
      props.entityType,
      props.entityId,
      documentsRef.current,
      ready,
      fileFieldKey,
    ]
  );

  const sanitizeValuesToFetch = useCallback((values: string[] | undefined) => {
    if (!values) {
      return [];
    }

    // Clear empty values
    const data = values?.filter((value) => value !== "");

    if (data.length === 0) {
      console.error("No values to fetch");
      return [];
    }

    return data;
  }, []);

  const handleSubmit = useCallback(
    async (value: FileFieldValue) => {
      props.onSubmit?.(value);

      const valueDocument = value as unknown as BoltDocument;

      let values = (documentIdsRef.current || []).concat(
        valueDocument?.id || ""
      );

      // Sanitize values to fetch
      values = sanitizeValuesToFetch(values);
      if (values.length === 0) {
        console.error("No values added to fetch after submit");
        return;
      }

      documentIdsRef.current = [...values];
      AppToasterV2.showSuccessAppToaster("Document Added");

      // This will trigger the allActionComplete afterwards
    },
    [props.onSubmit]
  );

  const removeDocumentHandler = useCallback(
    async (documentId: string) => {
      fileFieldRef.current?.remove({
        id: documentId,
      } as FileInfo);
      await boltDocumentApiClient.removeDocument(documentId);

      let values = documentIdsRef.current?.filter((x) => x !== documentId);

      // Sanitize values to fetch
      values = sanitizeValuesToFetch(values);
      if (values.length === 0) {
        documentsRef.current = [];
        AppToasterV2.showSuccessAppToaster("Document Removed");
        setFileFieldKey("");
        return;
      }

      documentIdsRef.current = [...values];

      AppToasterV2.showSuccessAppToaster("Document Removed");

      props.onRemoveDocument?.(documentId);

      // Call manually since its not part of the uploader itself
      handleAllActionComplete();
    },
    [boltDocumentApiClient, props.value]
  );

  const downloadDocumentHandler = useCallback(
    async (document: BoltDocument) => {
      const documentBlob = await documentModule.downloadDocument(document.id);
      documentModule.downloadFile(documentBlob, document.displayFileName);
    },
    [documentModule]
  );

  const handleAllActionComplete = useCallback(async () => {
    const docs = await boltDocumentApiClient.getDocuments(
      documentIdsRef.current || []
    );
    documentsRef.current = [...docs];
    setFileFieldKey(documentsRef.current.map((x) => x.id).join("---"));
  }, []);

  if (!ready) {
    return <LoadingV2 />;
  }

  return (
    <div
      className={classNames(
        "bolt-document-file-uploader bp5-form-group base-field",
        props.className,
        {
          "has-documents": documentsRef.current.length > 0,
        },
        fileFieldKey
      )}
      key={`fileField_${fileFieldKey}`}
    >
      <FileField
        ref={fileFieldRef}
        {...fileFieldProps}
        {...props}
        onSubmit={handleSubmit}
        onAllActionComplete={handleAllActionComplete}
        skipErrorMessagesHandling={true}
      />
      <div className="bolt-document-file-uploader__documents">
        {documentsRef.current.map((document) => (
          <div
            key={document.id}
            className="bolt-document-file-uploader__document"
          >
            <div className="bolt-document-file-uploader__document-name">
              {document.displayFileName}
            </div>
            <div className="bolt-document-file-uploader__document-actions">
              <ActionButtons
                actions={[
                  {
                    iconProps: {
                      icon: faDownload,
                    },
                    onActionClick: () => {
                      downloadDocumentHandler(document);
                    },
                  },
                  {
                    iconProps: {
                      icon: faTrash,
                    },
                    onActionClick: () => {
                      removeDocumentHandler(document.id);
                    },
                  },
                ]}
              />
            </div>
          </div>
        ))}
      </div>
      {props.errorMessages && (
        <FormFieldErrorContainer errorMessages={props.errorMessages} />
      )}
    </div>
  );
}
